import React, { useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { API } from '../api';
import PageHeader from '../ui/PageHeader';
import FilterBar from '../ui/FilterBar';
import useUrlFilters from '../ui/useUrlFilters';
import useAsync from '../ui/useAsync';
import DataTable, { ColumnsMenu, useColumnVisibility } from '../ui/DataTable';
import ListPage, { useListQuery } from '../ui/ListPage';
import ExportButton from './ExportPanel';
import EntityLink from '../ui/EntityLink';
import EmptyState from '../ui/EmptyState';
import Button from '../ui/Button';
import { environmentLabel, formatCount, formatDate, teamLabel } from '../ui/format';
import { hostPath } from '../ui/entityRoutes';
import useCycloneDx from './useCycloneDx';

const ENV_STORAGE_KEY = 'toggleState';

const muted = (text) => <span className="text-graphite">{text}</span>;
const dash = (value) => value || muted('—');
const countCell = (n) => (n ? formatCount(n) : muted('0'));
const severityCount = (n, className) => (n ? <span className={`font-semibold ${className}`}>{formatCount(n)}</span> : muted('0'));
const dateCell = (value) => <span className="text-graphite tabular-nums">{formatDate(value) || '—'}</span>;
const osOf = (h) => [h.os_name, h.os_version].filter(Boolean).join(' ');

// Keys and values match the URLs the legacy page wrote. os and os_mode are new.
const FILTER_DEFAULTS = { kw: '', codename_filter: '', os: 'all', os_mode: 'include', team: '', new_hosts: '', env: 'all', outdated: '' };

const hostnameFilter = { key: 'kw', type: 'search', label: 'Host', placeholder: 'Hostname', mono: true, width: 240 };
const envFilter = {
  key: 'env',
  type: 'select',
  label: 'Environment',
  defaultValue: 'all',
  options: [
    { value: 'all', label: 'All' },
    { value: 'bsg', label: 'BSG' },
    { value: 'enterprise', label: 'Enterprise' },
  ],
};
const teamFilter = {
  key: 'team',
  type: 'select',
  label: 'Team',
  options: [
    { value: '', label: 'All' },
    { value: 'dba', label: 'DBA' },
    { value: 'devops', label: 'DevOps' },
    { value: 'enterprise', label: 'Enterprise' },
  ],
};

const listFilters = (osNames, os) => [
  hostnameFilter,
  { key: 'codename_filter', type: 'search', label: 'Codename', placeholder: 'focal', width: 200 },
  {
    key: 'os',
    type: 'select',
    label: 'OS',
    defaultValue: 'all',
    options: [{ value: 'all', label: 'All' }, ...osNames.map((name) => ({ value: name, label: name }))],
  },
  ...(os !== 'all' ? [{
    key: 'os_mode',
    type: 'select',
    label: 'Match',
    defaultValue: 'include',
    options: [
      { value: 'include', label: 'Only this OS' },
      { value: 'exclude', label: 'All but this OS' },
    ],
  }] : []),
  teamFilter,
  {
    key: 'new_hosts',
    type: 'select',
    label: 'First seen',
    options: [
      { value: '', label: 'Any time' },
      { value: '1', label: 'Last 7 days' },
    ],
  },
  envFilter,
  { key: 'outdated', type: 'select', label: 'Packages', hidden: true, options: [{ value: '', label: 'All' }, { value: '1', label: 'Has outdated' }] },
];

const LIST_COLUMNS = [
  { key: 'hostname', header: 'Host', hideable: false, render: (h) => <EntityLink kind="host" id={h.id} name={h.hostname} /> },
  { key: 'vulns', header: 'Vulnerabilities', align: 'right', render: (h) => countCell(h.active_vuln_count) },
  { key: 'critical', header: 'Critical', align: 'right', render: (h) => severityCount(h.critical_vuln_count, 'text-crit') },
  { key: 'high', header: 'High', align: 'right', render: (h) => severityCount(h.high_vuln_count, 'text-high') },
  { key: 'packages', header: 'Packages', align: 'right', render: (h) => countCell(h.active_package_count) },
  { key: 'os', header: 'OS', render: (h) => dash(osOf(h)) },
  { key: 'codename', header: 'Codename', render: (h) => dash(h.codename) },
  { key: 'ip', header: 'IP address', mono: true, render: (h) => dash(h.ip) },
  { key: 'team', header: 'Team', render: (h) => dash(teamLabel(h.team_flow)) },
  { key: 'project', header: 'Project', defaultHidden: true, render: (h) => dash(h.team_project) },
  { key: 'region', header: 'Environment', render: (h) => dash(environmentLabel(h.region)) },
  { key: 'arch', header: 'Architecture', defaultHidden: true, render: (h) => dash(h.architecture) },
  { key: 'first_seen', header: 'First seen', defaultHidden: true, render: (h) => dateCell(h.first_seen_at) },
  { key: 'last_seen', header: 'Last seen', render: (h) => dateCell(h.last_seen_at) },
];

// API parameters for the current filters, as the legacy page sent them.
const apiParams = (filters) => {
  const params = {
    region: filters.env,
    team: filters.team || undefined,
    kw: filters.kw,
    new_hosts: filters.new_hosts ? 'true' : undefined,
    outdated: filters.outdated ? 'true' : undefined,
  };
  if (filters.codename_filter) params.codename = filters.codename_filter;
  else if (filters.os !== 'all') params[filters.os_mode === 'exclude' ? 'codename_exclude' : 'codename'] = filters.os;
  return params;
};

export default function HostsPage() {
  const location = useLocation();
  const search = new URLSearchParams(location.search);
  const [filters, setFilters] = useUrlFilters(FILTER_DEFAULTS);
  const osNames = useAsync(async () => [...new Set((await API.GET_OS_NAMES()).map((os) => (typeof os === 'string' ? os : os.codename)))].filter(Boolean), 'os');
  const filterDefs = listFilters(osNames.data || [], filters.os);
  const columnState = useColumnVisibility(LIST_COLUMNS, 'sbom-columns-hosts');
  const cyclonedx = useCycloneDx();

  // The environment is remembered between visits, as on the legacy page.
  const urlEnv = search.get('env');
  useEffect(() => {
    if (urlEnv) {
      localStorage.setItem(ENV_STORAGE_KEY, urlEnv);
      return;
    }
    const stored = localStorage.getItem(ENV_STORAGE_KEY);
    if (stored === 'bsg' || stored === 'enterprise') setFilters({ ...filters, env: stored });
  }, [urlEnv]); // eslint-disable-line react-hooks/exhaustive-deps

  const changeFilters = (next) => {
    localStorage.setItem(ENV_STORAGE_KEY, next.env || 'all');
    setFilters({ ...next, os_mode: next.os && next.os !== 'all' ? next.os_mode : 'include' });
  };

  const params = apiParams(filters);
  const key = JSON.stringify(params);
  const fetchPage = useCallback((paging) => API.GET_HOSTS({ ...params, ...paging }), [key]); // eslint-disable-line react-hooks/exhaustive-deps
  const query = useListQuery(fetchPage, key, 'hosts');

  const rowMenu = (h) => [
    { key: 'vulns', label: 'View vulnerabilities', to: hostPath(h, 'vulnerabilities') },
    { key: 'packages', label: 'View packages', to: hostPath(h, 'packages') },
    { key: 'divider', divider: true },
    { key: 'cyclonedx', label: cyclonedx.busy === h.hostname ? 'Downloading CycloneDX…' : 'Download CycloneDX', onSelect: () => cyclonedx.download(h.hostname) },
  ];

  const filtered = filterDefs.some((f) => (filters[f.key] ?? '') !== (f.defaultValue ?? ''));
  const empty = filtered ? (
    <EmptyState title="No hosts match these filters" action={<Button onClick={() => changeFilters(FILTER_DEFAULTS)}>Clear filters</Button>}>
      Host and codename match part of the name. Widen the other filters, or clear them.
    </EmptyState>
  ) : (
    <EmptyState title="No hosts found">
      Hosts appear here once their agents report an inventory.
    </EmptyState>
  );

  return (
    <ListPage>
      <PageHeader
        title="Hosts"
        actions={(
          <>
            <ColumnsMenu columns={LIST_COLUMNS} hidden={columnState.hidden} onToggle={columnState.toggle} onReset={columnState.reset} />
            <ExportButton env={filters.env} />
          </>
        )}
      />
      <FilterBar className="mb-3" filters={filterDefs} values={filters} onChange={changeFilters} />
      <DataTable
        className="flex-1"
        label="Hosts"
        columns={LIST_COLUMNS}
        hidden={columnState.hidden}
        rows={query.rows}
        rowKey={(h) => h.id}
        rowLabel={(h) => h.hostname}
        rowMenu={rowMenu}
        loading={query.loading}
        error={query.error}
        errorTitle="Hosts could not be loaded"
        onRetry={query.retry}
        empty={empty}
        pagination={query.pagination}
      />
    </ListPage>
  );
}
